import React, { useState, useEffect } from 'react'
import axios from 'axios';
import Select from 'react-select';
import { baseUrl } from "../../../comman/config";
import { can } from "../../../comman/permissions";
import { useNavigate, Link } from 'react-router-dom';
import { menuAboveTable } from "../../../comman/selectMenu";

// Department Master: the collapsible groups in the side bar, and which screens hang under each.
function List() {

    const [departmentList, setDepartmentList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    // Status defaults to Active, matching the other master lists. '' is All.
    const [statusFilter, setStatusFilter] = useState({ value: '1', label: 'Active' });
    // Which screens to narrow the list to. Nothing picked is no filter at all; anything picked
    // keeps the departments holding at least one of them, since a screen can sit under more than
    // one department and asking for two screens usually means "either", not "both".
    const [screenFilter, setScreenFilter] = useState([]);
    const [screenOptions, setScreenOptions] = useState([]);

    // role 0 = superadmin, 1 = admin. Both may enable and disable; only the superadmin may delete.
    const role = String(localStorage.getItem('userRole'));
    // Which buttons this user gets is Role Master's call now, not a role number's -- see
    // comman/permissions.js. `role` is still read because it is what the delete and enable
    // endpoints are told to check against.
    const canDisable = can('disable');
    const canActivate = can('active');
    const canDelete = can('delete');

    const navigate = useNavigate();

    useEffect(() => {
        getDepartmentList();
        getScreenOptions();
    }, []);

    useEffect(() => {
        const query = searchQuery.trim().toLowerCase();
        const status = statusFilter?.value ?? '';
        const wantedScreens = screenFilter.map((screen) => screen.value);
        setFilteredList(departmentList.filter((item) => {
            const screens = item.screens ?? [];
            const screenNames = screens.map((screen) => screen.name).join(' ').toLowerCase();
            const matchesQuery = !query
                || (item.name ?? '').toLowerCase().includes(query)
                || screenNames.includes(query);
            const matchesStatus = status === '' || String(item.status) === status;
            const matchesScreens = wantedScreens.length === 0
                || screens.some((screen) => wantedScreens.includes(screen.id));
            return matchesQuery && matchesStatus && matchesScreens;
        }));
    }, [searchQuery, statusFilter, screenFilter, departmentList]);

    const getDepartmentList = async () => {
        try {
            const response = await axios.get(`${baseUrl}/department_master/list`);
            setDepartmentList(response.data.data);
        } catch (error) {
            console.log(error);
        }
    }

    // The dropdown offers every screen, disabled ones included: a department can still hold one,
    // and it stays findable rather than dropping silently out of the filter.
    const getScreenOptions = async () => {
        try {
            const response = await axios.get(`${baseUrl}/screen_master/list`);
            const screens = response.data?.data ?? [];
            setScreenOptions(screens.map((screen) => ({
                value: screen.id,
                label: `${screen.name} (${screen.path})`,
            })));
        } catch (error) {
            console.log(error);
        }
    };

    const handelFilterChange = (e) => {
        const { value } = e.target;
        setSearchQuery(value);
    };

    const handleClear = () => {
        setSearchQuery('');
        setStatusFilter({ value: '1', label: 'Active' });
        setScreenFilter([]);
    };

    const deleteDepartment = async (id) => {
        // The sentence about the side bar has been taken off at the client's request. Note that
        // it was accurate: sidebarMenu selects departments WHERE status = 1, so a disabled
        // department and every screen it alone holds do leave the menu. If Department is to be a
        // purely virtual grouping, as the request describes it, that filter is what has to go.
        const shouldDelete = window.confirm("Are you sure you want to disable this department?");
        if (shouldDelete) {
            try {
                await axios.delete(`${baseUrl}/department_master/delete/${id}`, { data: { role } });
                window.location.reload();
            } catch (error) {
                alert(error?.response?.data?.msg ?? 'Could not disable the department.');
            }
        }
    };

    const enableDepartment = async (id) => {
        if (!window.confirm("Enable this department?")) return;
        try {
            await axios.post(`${baseUrl}/department_master/enable/${id}`, { role });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not enable the department.');
        }
    };

    // Delete removes the row, unlike Disable which is recoverable, so its confirmation says so.
    // The screens themselves survive -- only the grouping goes.
    const destroyDepartment = async (id) => {
        if (!window.confirm("Permanently DELETE this department? Its screens are kept; only the group goes. Disable is reversible; this is not.")) return;
        try {
            await axios.delete(`${baseUrl}/department_master/destroy/${id}`, { data: { role } });
            window.location.reload();
        } catch (error) {
            alert(error?.response?.data?.msg ?? 'Could not delete the department.');
        }
    };

    const editDepartment = async (id) => {
        navigate(`/department_master/edit/${btoa(id)}`, { state: departmentList.filter(item => item.id == id) });
    }

    return (
        <div className="p-4">
            <div className="flex justify-between items-center py-4 px-6">
                <h1 className="text-lg sm:text-xl md:text-xl lg:text-xl xl:text-2xl font-bold">Department</h1>
                <div>
                    <Link to="/department_master/add">
                        <button className="px-4 py-2 bg-blue-500 text-white rounded-md mr-4 hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">Add Department</button>
                    </Link>
                </div>
            </div>
            <div className="relative shadow-md sm:rounded-lg">
                <div className="pb-4 flex flex-column sm:flex-row flex-wrap space-y-4 sm:space-y-0 items-center justify-between">
                    <label htmlFor="table-search" className="sr-only">Search</label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 rtl:inset-r-0 rtl:right-0 flex items-center ps-3 pointer-events-none">
                            <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path></svg>
                        </div>
                        <input type="text" value={searchQuery} onChange={handelFilterChange} id="table-search" className="block p-2 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500" placeholder="Search for departments" />
                    </div>
                    <div className="flex items-center gap-4 ms-4 me-auto">
                        <Select
                            {...menuAboveTable}
                            isMulti
                            classNamePrefix="select"
                            className="basic-multi-select min-w-64"
                            value={screenFilter}
                            name="screens"
                            onChange={(selected) => setScreenFilter(selected ?? [])}
                            options={screenOptions}
                            placeholder="Screen"
                        />
                        <Select
                            {...menuAboveTable}
                            classNamePrefix="select"
                            className="basic-multi-select w-36"
                            value={statusFilter}
                            name="status"
                            onChange={(selected) => setStatusFilter(selected)}
                            options={[
                                { value: '', label: 'All' },
                                { value: '1', label: 'Active' },
                                { value: '0', label: 'Inactive' }
                            ]}
                            placeholder="Status"
                            isClearable={false}
                        />
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200"
                        >
                            Clear
                        </button>
                    </div>
                </div>
                {/* The header stays put while the rows scroll under it. The height cap is what
                    makes that work -- a container with no limit never scrolls, and a sticky
                    header has nothing to hold itself against. */}
                <div className="overflow-auto max-h-[65vh]">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Department Name</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Screen</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Status</th>
                            <th scope="col" className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 px-6 py-3">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredList.map((item) => (
                            <tr key={item.id} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">{item.name}</th>
                                <td className="px-6 py-4">
                                    {(item.screens ?? []).map((screen) => screen.name).join(', ') || '-'}
                                </td>
                                <td className="px-6 py-4">{item.id === 0 ? '-' : (item.status == 1 ? 'Active' : 'Inactive')}</td>
                                <td className="px-6 py-4">
                                    {/* Department 0 is where a screen waits until a real
                                        department takes it. Its membership is worked out, not
                                        chosen, so there is nothing here to edit, disable or
                                        delete -- and the endpoints refuse it too. */}
                                    {item.id === 0 ? <span className="text-gray-500">-</span> : <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => editDepartment(item.id)}
                                            className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-blue-200">
                                            Edit
                                        </button>
                                        {item.status == 1 && canDisable && <button
                                            type="button"
                                            onClick={() => deleteDepartment(item.id)}
                                            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring focus:ring-red-200">
                                            Disable
                                        </button>}
                                        {item.status == 0 && canActivate && <button
                                            type="button"
                                            onClick={() => enableDepartment(item.id)}
                                            className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring focus:ring-green-200">
                                            Enable
                                        </button>}
                                        {canDelete && <button
                                            type="button"
                                            onClick={() => destroyDepartment(item.id)}
                                            className="px-3 py-1 text-sm bg-red-700 text-white rounded-md hover:bg-red-800 focus:outline-none focus:ring focus:ring-red-300">
                                            Delete
                                        </button>}
                                    </div>}
                                </td>
                            </tr>))}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

export default List;
